// EmailVerification.jsx - Email verification page with OTP input
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useVerifyEmailMutation } from '../hooks/auth';
import { ROUTES } from '../constants/Routes';
import { getLogoutRedirect } from '../utils/logoutRedirect';
import AuthLayout from '../components/auth/AuthLayout';
import { AUTH_LINK_CLASS } from '../components/auth/authLinkStyles';
import AuthHeader from '../components/auth/AuthHeader';
import PrimaryButton from '../components/auth/PrimaryButton';
import { ArrowLeft } from 'lucide-react';

// Simple PageTitle component
const PageTitle = ({ title }) => {
  React.useEffect(() => {
    document.title = title || 'Bloom IQ';
  }, [title]);
  return null;
};

const EmailVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const verifyEmailMutation = useVerifyEmailMutation();

  // Get email from navigation state or require user to enter it
  const emailFromState = location.state?.email || '';

  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const inputRefs = [
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
  ];

  // Auto-focus first input on mount
  useEffect(() => {
    inputRefs[0].current?.focus();
  }, []);

  // Handle input change
  const handleChange = (index, value) => {
    // Only accept numbers
    if (value && !/^[0-9]$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs[index + 1].current?.focus();
    }

    // Auto-submit when all 6 digits are entered
    if (value && index === 5) {
      const fullCode = newCode.join('');
      if (fullCode.length === 6) {
        handleVerify(fullCode);
      }
    }
  };

  // Handle backspace
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs[index - 1].current?.focus();
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);

    if (!/^\d+$/.test(pastedData)) {
      return;
    }

    const newCode = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
    setCode(newCode);

    // Focus last filled input or first empty
    const nextEmptyIndex = newCode.findIndex((digit) => !digit);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs[focusIndex].current?.focus();

    // Auto-submit if complete
    if (pastedData.length === 6) {
      handleVerify(pastedData);
    }
  };

  // Handle verification
  const handleVerify = async (verificationCode) => {
    if (!emailFromState) {
      setError('Email not found. Please start from sign up.');
      return;
    }

    const codeToVerify = verificationCode || code.join('');

    if (codeToVerify.length !== 6) {
      setError('Please enter the complete 6-digit code');
      return;
    }

    try {
      await verifyEmailMutation.mutateAsync({
        email: emailFromState,
        code: codeToVerify,
      });

      setSuccess(true);
      setError('');

      // Navigate to Bloom Terminal after 2 seconds
      setTimeout(() => {
        navigate(ROUTES.BLOOMTERMINAL);
      }, 2000);
    } catch (error) {
      console.error('Verification error:', error);
      setError(error.message || 'Invalid verification code. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs[0].current?.focus();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    handleVerify();
  };

  return (
    <>
      <PageTitle title="Email Verification - Bloom IQ" />
      <AuthLayout variant="light">
        <div className="w-full">
          {/* Back Button */}
          <Link
            to={getLogoutRedirect()}
            className={`mb-6 inline-flex items-center ${AUTH_LINK_CLASS}`}
          >
            <ArrowLeft size={20} className="mr-2" />
            <span>Back to Login</span>
          </Link>

          {/* Logo and Title */}
          <AuthHeader
            title="Verification"
            subtitle="Please enter the code, we sent you via email"
          />

          {/* Success message */}
          {success && (
            <div className="bg-[#E7F6EE] border border-[#DCE7DF] text-[#15803D] p-4 rounded-md mb-5 text-sm">
              Email verified successfully! Redirecting to Analytics...
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-md mb-5 text-sm">
              {error}
            </div>
          )}

          {/* Verification Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* OTP Input - 6 circular inputs */}
            <div className="flex justify-center gap-3">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={inputRefs[index]}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={handlePaste}
                  className="w-14 h-14 text-center text-2xl font-medium bg-white border-2 border-[#E8EBE9] focus:border-bloom-green rounded-full text-[#282A29] focus:outline-none focus:ring-2 focus:ring-bloom-green/40 transition-all"
                  aria-label={`Digit ${index + 1}`}
                  disabled={verifyEmailMutation.isPending || success}
                />
              ))}
            </div>

            {/* Verify Button */}
            <PrimaryButton
              type="submit"
              loading={verifyEmailMutation.isPending}
              disabled={verifyEmailMutation.isPending || success}
            >
              Verify
            </PrimaryButton>
          </form>

          {/* Resend code link */}
          <div className="mt-6 text-center">
            <p className="text-sm text-[#5d625f] font-['Inter']">
              Didn't receive the code?{' '}
              <button
                type="button"
                onClick={() => {
                  // TODO: Implement resend logic
                  alert('Resend functionality to be implemented');
                }}
                className={AUTH_LINK_CLASS}
              >
                Resend
              </button>
            </p>
          </div>
        </div>
      </AuthLayout>
    </>
  );
};

export default EmailVerification;
